import { css, cx } from "@emotion/css";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MuiMenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popper from "@mui/material/Popper";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { useSubmenuHover } from "src/components/NewComponents/menuActions/useSubmenuHover";
import isMobile from "src/helpers/isMobile";

import { SwipeableDrawer } from "./SwipableDrawer";

export interface MenuItemConfig {
	className?: string;
	onClick?: (event: React.MouseEvent) => void;
	selected?: boolean;
	disabled?: boolean;
	Icon?: ReactNode;
	title: ReactNode;
	description?: ReactNode;
	itemTextClassName?: string;
	testId?: string;
	items?: MenuItemConfig[];
}

export interface MenuDividerConfig {
	isDivider: true;
}

export type MenuEntry = MenuItemConfig | MenuDividerConfig;

interface MenuProps {
	items?: (MenuEntry | null)[];
	Actions?: ReactNode;
	closeAfterClick?: boolean;
	onClose?: () => void;
	Component?: React.ElementType;
	renderTrigger?: (params: {
		onClick: (e: React.MouseEvent) => void;
		anchor: HTMLElement | null;
	}) => ReactNode;
	renderHeader?: () => ReactNode;
	renderContent?: () => ReactNode;
	title?: ReactNode;
	titleClassName?: string;
	disabled?: boolean;
	buttonClassName?: string;
	buttonTestId?: string;
	menuPopperClassName?: string;
	itemsClassName?: string;
	className?: string;
	triggerWrapperClassName?: string;
	icon?: ReactNode;
	tooltip?: string;
	anchorId?: string;
	SwipeAreaProps?: object;
}

function MenuComponent(props: MenuProps) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [mobileSubmenu, setMobileSubmenu] = useState<MenuItemConfig | null>(
		null,
	);
	const { activeSubmenu, close: closeSubmenu, getHandlers } = useSubmenuHover();

	const closeMenu = () => {
		setAnchor(null);
		setMobileSubmenu(null);
		closeSubmenu();
		props.onClose?.();
	};

	const renderMenuItem = (item: MenuItemConfig, key: number | string) => {
		const isNested = !!item.items?.length;
		const hoverHandlers =
			isNested && !isMobile() ? getHandlers(item as never) : {};

		return (
			<MuiMenuItem
				className={cx("menu-item", item.className)}
				key={key}
				onClick={(e) => {
					e.stopPropagation();
					if (isNested) {
						if (isMobile()) {
							setMobileSubmenu(item);
						} else {
							closeSubmenu();
						}
						return;
					}
					closeSubmenu();
					item.onClick?.(e);
					props.closeAfterClick && setAnchor(null);
				}}
				disableRipple
				selected={item.selected}
				disabled={item.disabled}
				data-test-id={item.testId}
				{...(isNested ? { "data-no-close": true } : {})}
				{...hoverHandlers}
			>
				{item.description ? (
					<div className="flex flex-col flex-1 gap-4">
						<div className="flex items-center gap-8">
							{item.Icon}
							<span className={cx("size-16-light", item.itemTextClassName)}>
								{item.title}
							</span>
						</div>
						<span className="size-14-light text-grey ws-normal">
							{item.description}
						</span>
					</div>
				) : (
					<>
						{item.Icon && (
							<ListItemIcon
								className={css`
                  min-width: 0 !important;
                `}
							>
								{item.Icon}
							</ListItemIcon>
						)}
						<span
							className={cx("size-14-light flex-1", item.itemTextClassName)}
						>
							{item.title}
						</span>
					</>
				)}
				{isNested && <ChevronRight className="icon-small" />}
			</MuiMenuItem>
		);
	};

	const renderItems = () => {
		if (props.Actions) {
			return (
				<div
					onClickCapture={(e) => {
						if ((e.target as HTMLElement).closest("[data-no-close]")) return;
						setTimeout(closeMenu, 0);
					}}
				>
					{props.Actions}
				</div>
			);
		}

		if (!props.items) {
			return null;
		}

		if (isMobile() && mobileSubmenu) {
			return (
				<>
					<MuiMenuItem
						disableRipple
						className="menu-item"
						data-no-close
						onClick={(e) => {
							e.stopPropagation();
							setMobileSubmenu(null);
						}}
					>
						<ListItemIcon
							className={css`
                min-width: 0 !important;
              `}
						>
							<ArrowLeft className="icon-small" />
						</ListItemIcon>
						<div className="flex flex-col">
							<span className="size-14-light">{mobileSubmenu.title}</span>
						</div>
					</MuiMenuItem>
					{(mobileSubmenu.items ?? []).map((subItem, index) =>
						renderMenuItem(subItem, `${mobileSubmenu.title}-${index}`),
					)}
				</>
			);
		}

		const renderedItems = props.items.map((item, i) => {
			if (!item) {
				return null;
			}

			if ("isDivider" in item) {
				return <div key={i} className="menu-divider" />;
			}

			return renderMenuItem(item, i);
		});

		if (props.itemsClassName) {
			return <div className={props.itemsClassName}>{renderedItems}</div>;
		}

		return renderedItems;
	};

	const renderSubmenuPopper = () => {
		if (!activeSubmenu || props.Actions) {
			return null;
		}

		return (
			<ClickAwayListener onClickAway={closeSubmenu}>
				<Popper
					open
					anchorEl={activeSubmenu.anchor}
					placement="right-start"
					className="popper"
				>
					<MenuList disablePadding>
						{(activeSubmenu.item.items ?? []).map((subItem, index) => (
							<MuiMenuItem
								key={subItem.testId ?? `${subItem.title}-${index}`}
								onClick={(e) => {
									e.stopPropagation();
									subItem.onClick?.(e);
									closeSubmenu();
									props.closeAfterClick && setAnchor(null);
								}}
								disableRipple
								className={cx("menu-item", subItem.className)}
								data-test-id={subItem.testId}
							>
								{subItem.description ? (
									<div className="flex flex-col gap-4">
										<div className="flex items-center gap-8">
											{subItem.Icon}
											<span
												className={cx(
													"size-16-light",
													subItem.itemTextClassName,
												)}
											>
												{subItem.title}
											</span>
										</div>
										<span className="size-14-light text-grey ws-normal">
											{subItem.description}
										</span>
									</div>
								) : (
									<>
										{subItem.Icon && (
											<ListItemIcon
												className={css`
                        min-width: 0 !important;
                      `}
											>
												{subItem.Icon}
											</ListItemIcon>
										)}
										<span
											className={cx("size-14-light", subItem.itemTextClassName)}
										>
											{subItem.title}
										</span>
									</>
								)}
							</MuiMenuItem>
						))}
					</MenuList>
				</Popper>
			</ClickAwayListener>
		);
	};

	const renderHeader = () => {
		if (!props.renderHeader) {
			return null;
		}

		return <div className="flex px-16 py-8">{props.renderHeader()}</div>;
	};

	const renderTitle = () => {
		if (!props.title) {
			return null;
		}

		return (
			<div className={cx("flex px-16", props.titleClassName)}>
				<span className="size-16-light text-black">{props.title}</span>
			</div>
		);
	};

	const renderContent = () => {
		if (!props.renderContent) {
			return null;
		}

		return <div className="p-16">{props.renderContent()}</div>;
	};

	const renderMenu = () => {
		if (!isMobile()) {
			return (
				<>
					<ClickAwayListener
						onClickAway={() => {
							setAnchor(null);
							closeSubmenu();
							props.onClose?.();
						}}
					>
						<Popper
							open={!!anchor}
							anchorEl={anchor}
							placement="bottom-end"
							className={cx("popper", props.menuPopperClassName)}
						>
							{renderTitle()}
							<MenuList disablePadding>{renderItems()}</MenuList>
						</Popper>
					</ClickAwayListener>
					{renderSubmenuPopper()}
				</>
			);
		}

		return (
			<SwipeableDrawer
				open={!!anchor}
				onClose={() => {
					setAnchor(null);
					setMobileSubmenu(null);
					props.onClose?.();
				}}
				onOpen={(e) => setAnchor(e.currentTarget as HTMLElement)}
				SwipeAreaProps={props.SwipeAreaProps}
			>
				{renderHeader()}
				{renderTitle()}
				<MenuList disablePadding>{renderItems()}</MenuList>
				{renderContent()}
			</SwipeableDrawer>
		);
	};

	const renderTrigger = () => {
		const Component = props.Component || IconButton;

		if (props.renderTrigger) {
			return props.renderTrigger({
				onClick: (e) => {
					e.stopPropagation();
					e.preventDefault();
					setAnchor(anchor ? null : (e.currentTarget as HTMLElement));
				},
				anchor,
			});
		}

		return (
			<Component
				aria-label="toggle"
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					e.stopPropagation();
					e.preventDefault();
					setAnchor(e.currentTarget);
				}}
				aria-controls={anchor !== null ? anchor : undefined}
				aria-haspopup="true"
				aria-expanded={anchor !== null ? "true" : undefined}
				disabled={props.disabled}
				className={cx(props.buttonClassName, "menu-icon-button")}
				data-test-id={props.buttonTestId}
				disableRipple
			>
				{props.icon}
			</Component>
		);
	};

	return (
		<div className={cx("flex items-center justify-start", props.className)}>
			<Tooltip
				title={props.tooltip || null}
				wrapperClassName={props.triggerWrapperClassName}
			>
				{renderTrigger()}
			</Tooltip>
			{renderMenu()}
		</div>
	);
}

export default MenuComponent;
export { MenuComponent as Menu };
